/**
 * Safe storage utilities that handle iOS Safari limitations
 * and private browsing mode gracefully
 */

interface StorageOptions {
  fallbackValue?: any;
  useSessionStorage?: boolean;
}

/**
 * Safely get item from storage with iOS compatibility
 */
export function safeGetItem(
  key: string, 
  options: StorageOptions = {}
): string | null {
  const { fallbackValue = null, useSessionStorage = false } = options;
  
  try {
    const storage = useSessionStorage ? sessionStorage : localStorage;
    const value = storage.getItem(key);
    return value !== null ? value : fallbackValue;
  } catch (error) {
    // iOS Safari in private mode or storage full
    console.warn(`[SafeStorage] Failed to get ${key}:`, error);
    return fallbackValue;
  }
}

/**
 * Safely set item in storage with iOS compatibility
 */
export function safeSetItem(
  key: string, 
  value: string,
  options: StorageOptions = {}
): boolean {
  const { useSessionStorage = false } = options;
  
  try {
    const storage = useSessionStorage ? sessionStorage : localStorage;
    storage.setItem(key, value);
    return true;
  } catch (error) {
    // iOS Safari in private mode or storage full
    console.warn(`[SafeStorage] Failed to set ${key}:`, error);
    
    // Try to clear some space if storage is full
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      try {
        // Clear old items starting with 'storybaib_temp_'
        const storage = useSessionStorage ? sessionStorage : localStorage;
        const keysToRemove: string[] = [];
        
        for (let i = 0; i < storage.length; i++) {
          const storageKey = storage.key(i);
          if (storageKey?.startsWith('storybaib_temp_')) {
            keysToRemove.push(storageKey);
          }
        }
        
        keysToRemove.forEach(k => storage.removeItem(k));
        
        // Try again
        storage.setItem(key, value);
        return true;
      } catch (retryError) {
        console.error(`[SafeStorage] Retry failed for ${key}:`, retryError);
      }
    }
    
    return false;
  }
}

/**
 * Safely remove item from storage
 */
export function safeRemoveItem(
  key: string,
  options: StorageOptions = {}
): boolean {
  const { useSessionStorage = false } = options;
  
  try {
    const storage = useSessionStorage ? sessionStorage : localStorage;
    storage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`[SafeStorage] Failed to remove ${key}:`, error);
    return false;
  }
}

/**
 * Check if storage is available (especially for iOS private browsing)
 */
export function isStorageAvailable(type: 'localStorage' | 'sessionStorage' = 'localStorage'): boolean {
  try {
    const storage = type === 'localStorage' ? localStorage : sessionStorage;
    const testKey = '__storage_test__';
    storage.setItem(testKey, 'test');
    storage.removeItem(testKey);
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Get parsed JSON from storage safely
 */
export function safeGetJSON<T>(
  key: string,
  fallbackValue: T,
  options: StorageOptions = {}
): T {
  const value = safeGetItem(key, options);
  
  if (!value) return fallbackValue;
  
  try {
    return JSON.parse(value) as T;
  } catch (error) {
    console.warn(`[SafeStorage] Failed to parse JSON for ${key}:`, error);
    return fallbackValue;
  }
}

/**
 * Set JSON in storage safely
 */
export function safeSetJSON(
  key: string,
  value: any,
  options: StorageOptions = {}
): boolean {
  try {
    const jsonString = JSON.stringify(value);
    return safeSetItem(key, jsonString, options);
  } catch (error) {
    console.warn(`[SafeStorage] Failed to stringify JSON for ${key}:`, error);
    return false;
  }
}

/**
 * Storage keys used in the app
 */
export const STORAGE_KEYS = {
  EMAIL_COLLECTED: 'storybaib_email_collected',
  EMAIL_POPUP_SHOWN: 'storybaib_email_popup_shown',
  HUMAN_VERIFIED: 'storybaib_human_verified',
  MULTI_CHARACTER_BOOK: 'isMultiCharacterBook',
  CHARACTER_COUNT: 'characterCount',
  BOOK_STYLE: 'bookStyle',
  SELECTED_THEME: 'selectedTheme',
  USER_PREFERENCES: 'userPreferences',
} as const;
